import React, { CSSProperties, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { trackMixpanel, useNavigate } from "utils";

import { Checkbox, Divider, Flex, Icon, IconSizes, Switch, Tooltip } from "components";

type WithIcon = {
  icon?: ReactNode;
  iconName?: never;
};

type WithIconName = {
  icon?: never;
  iconName?: string;
};

type BaseContextMenuItemProps = {
  className?: string;
  color?: string;
  details?: ReactNode;
  dataTestId?: string;
  iconPlacement?: string;
  hasCheckbox?: boolean;
  hasToggle?: boolean;
  index?: number;
  isChecked?: boolean;
  isToggled?: boolean;
  label?: string | ReactNode;
  state?: "warning" | "disabled";
  style?: CSSProperties;
  tooltip?: ReactNode;
  url?: string;
  onCheck?: (checked: boolean) => void;
  onClick?: (e: MouseEvent) => void;
  onToggle?: (checked: boolean) => void;
};

type ContextMenuItemPropsWithIcon = BaseContextMenuItemProps & WithIcon;
type ContextMenuItemPropsWithIconName = BaseContextMenuItemProps & WithIconName;

export type ContextMenuItemProps = ContextMenuItemPropsWithIcon | ContextMenuItemPropsWithIconName;

export type ContextMenuProps = {
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  iconPlacement?: string;
  iconSize?: IconSizes;
  items: ContextMenuItemProps[];
  placement: "right" | "left" | "bottom-left" | "bottom-right" | "top-left" | "top-right";
  state?: string;
  visible: boolean;
  onClose: () => void;
};

const getPlacementStyle = (placement: ContextMenuProps["placement"], parentDiv: HTMLDivElement | null, menuDiv: HTMLDivElement | null) => {
  const defaults = {
    position: "fixed" as React.CSSProperties["position"],
    maxHeight: "calc(100vh - 80px)", // Account for header height
    overflowY: "auto" as const, // Enable scrolling for long menus
  };

  if (!parentDiv || !menuDiv) {
    return defaults;
  }

  const { top, left, bottom, right } = parentDiv.getBoundingClientRect();
  const menuWidth = menuDiv.offsetWidth; // Get the width of the menu
  const headerHeight = 80; // Assuming header height is 80px
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const menuHeight = 400; // Approximate max height of menu
  const bottomPadding = 16; // Padding from bottom of viewport

  if (placement === "right") {
    const wouldOverflowBottom = top + menuHeight > viewportHeight - bottomPadding;
    const adjustedTop = wouldOverflowBottom ? Math.max(headerHeight, viewportHeight - menuHeight - bottomPadding) : top;

    return {
      ...defaults,
      top: `${adjustedTop + 20}px`,
      left: `calc(${right}px + 1rem)`,
    };
  }
  if (placement === "left") {
    return {
      ...defaults,
      top: `${top}px`,
      left: `calc(${left}px - ${menuWidth + 16}px)`,
    };
  }
  const getVerticalPlacement = () => {
    const wouldOverflowBottom = bottom + menuHeight > viewportHeight - bottomPadding;
    return wouldOverflowBottom
      ? { top: "auto" as const, bottom: `calc(${viewportHeight - top}px + 0.5rem)` }
      : { top: `calc(${bottom}px + 1rem)`, bottom: undefined };
  };

  if (placement === "bottom-left") {
    return { ...defaults, ...getVerticalPlacement(), left: `${left}px` };
  }
  if (placement === "bottom-right") {
    return { ...defaults, ...getVerticalPlacement(), right: `${viewportWidth - right}px`, left: "auto" };
  }
  // top-* placements anchor the menu above the trigger — used by triggers that sit at the bottom of the viewport
  if (placement === "top-left") {
    return {
      ...defaults,
      bottom: `calc(${viewportHeight - top}px + 0.5rem)`,
      left: `${left}px`,
      top: "auto",
    };
  }
  if (placement === "top-right") {
    return {
      ...defaults,
      bottom: `calc(${viewportHeight - top}px + 0.5rem)`,
      right: `${viewportWidth - right}px`,
      left: "auto",
      top: "auto",
    };
  }
};

const ContextMenuItem = ({
  className,
  dataTestId,
  details,
  icon: CtaIcon,
  iconPlacement = "left",
  index,
  hasCheckbox,
  hasToggle,
  isChecked,
  isToggled,
  label,
  state,
  style,
  tooltip,
  onCheck,
  onClick,
  onToggle,
}: ContextMenuItemPropsWithIcon) => {
  const menuItem = (
    <div
      className={["menu-item", iconPlacement === "right" && "icon-right", state && `label ${state}`, className].filter(Boolean).join(" ")}
      data-testid={`${dataTestId}-item-${index}`}
      onClick={(e) => {
        trackMixpanel("ClickContextMenuItem", { label });

        if (hasToggle && state !== "disabled") {
          onToggle?.(!isToggled);
        }

        onClick?.(e);
      }}
      style={style}
    >
      <Flex gap="1rem" align="center">
        {iconPlacement === "left" && CtaIcon}
        <span className={`${state ? `${state}` : "label"}`}>{label}</span>
        <div className="menu-item-controls">
          {hasToggle && (
            <Switch
              checked={isToggled}
              onChange={(checked: boolean, event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                if (state !== "disabled") {
                  onToggle?.(checked);
                }
              }}
              disabled={state === "disabled"}
              data-testid={`${dataTestId}-toggle`}
            />
          )}
          {hasCheckbox && (
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                if (state !== "disabled") {
                  onCheck?.(e.target.checked);
                }
              }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              disabled={state === "disabled"}
              data-testid={`${dataTestId}-checkbox`}
            />
          )}
        </div>
        {iconPlacement === "right" && CtaIcon}
      </Flex>
      {details && <div className="details">{details}</div>}
    </div>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="left">
      {menuItem}
    </Tooltip>
  ) : (
    menuItem
  );
};

export const ContextMenu = ({
  className = "",
  children,
  items,
  placement = "right",
  iconPlacement = "left",
  iconSize = "m",
  visible,
  onClose,
  dataTestId,
}: ContextMenuProps) => {
  const navigate = useNavigate();
  const parentRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [placementStyle, setPlacementStyle] = useState(getPlacementStyle(placement, parentRef.current, menuRef.current));

  useEffect(() => {
    const updatePlacement = () => {
      setPlacementStyle(getPlacementStyle(placement, parentRef.current, menuRef.current));
    };

    updatePlacement();

    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, { passive: true });

    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement);
    };
  }, [placement, visible]);

  const handleDrawerClose = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return (
    <div className="context-menu-container" data-testid={`${dataTestId}-container`} ref={parentRef}>
      {children}
      {visible &&
        createPortal(
          <>
            <div className="context-menu-mask" data-testid="context-menu-mask" onClick={handleDrawerClose} />
            <div
              className={`${className} context-menu-content`}
              style={placementStyle}
              ref={menuRef}
              data-testid={`${dataTestId}-content`}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item, index) => {
                const blockClick = item.hasCheckbox || item.hasToggle || item.state === "disabled";
                return (
                  <div key={index} className="context-menu-item" data-testid={`${dataTestId}-content-${index}`}>
                    <ContextMenuItem
                      {...item}
                      dataTestId={dataTestId}
                      icon={item.iconName ? <Icon iconName={item.iconName} color={item.color} size={iconSize} /> : item.icon ?? null}
                      iconName={undefined}
                      iconPlacement={iconPlacement}
                      index={index}
                      label={item.label}
                      state={item.state}
                      onClick={
                        blockClick
                          ? (e) => e.stopPropagation()
                          : item.url
                          ? () => {
                              navigate(item.url as string);
                              trackMixpanel("ClickLink", {
                                linkText: item.label,
                                link: item.url,
                              });
                            }
                          : (e) => {
                              handleDrawerClose(e);
                              item.onClick?.(e);
                            }
                      }
                    />
                    {index < items.length - 1 ? <Divider /> : null}
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
